import { ReactElement } from 'react';
import { ListBox } from '@sc/shared-ui';
import {
  useApolloClient,
  useMutation,
  useSuspenseQuery,
} from '@apollo/client/react';
import { getFragmentData } from '@sc/graphql';
import {
  GQL_FRAGMENT_THEME,
  GQL_MUTATION_UPDATE_PROJECT,
  GQL_QUERY_INDEX_THEMES,
} from '../utility/queries';
import { notify } from '../utility/notify';
import { track } from '../utility/analytics';

const DEFAULT_THEME_LABEL = 'Default';

export const ProjectThemePicker = ({
  projectUuid,
  currentThemeName,
}: {
  projectUuid: string;
  currentThemeName: string | null;
}): ReactElement => {
  const client = useApolloClient();

  const { data } = useSuspenseQuery(GQL_QUERY_INDEX_THEMES, {
    variables: { input: { pagination: { page: 1, perPage: 50 } } },
  });

  const [updateProject] = useMutation(GQL_MUTATION_UPDATE_PROJECT, {
    onCompleted: () => {
      notify.success('Updated theme.');

      track('Theme Applied To Project', { projectUuid, source: 'picker' });
    },
  });

  const themes = data.indexThemes.items.map((i) =>
    getFragmentData(GQL_FRAGMENT_THEME, i)
  );

  const options = [
    { label: DEFAULT_THEME_LABEL, value: DEFAULT_THEME_LABEL },
    ...themes.map((t) => ({ label: t.name, value: t.name })),
  ];

  return (
    <ListBox
      label="Brand theme"
      options={options}
      selected={currentThemeName ?? DEFAULT_THEME_LABEL}
      setSelected={(name) => {
        const theme = themes.find((t) => t.name === name) ?? null;

        client.cache.modify({
          id: `Project:${projectUuid}`,
          fields: {
            theme: () =>
              theme
                ? {
                    __typename: 'ProjectTheme',
                    uuid: theme.uuid,
                    name: theme.name,
                    brandColour: theme.brandColour,
                    themeType: theme.themeType,
                    title: theme.title ?? null,
                    subtitle: theme.subtitle ?? null,
                    logoSrc: theme.logo.src,
                  }
                : null,
          },
        });

        void updateProject({
          variables: {
            input: {
              uuid: projectUuid,
              themeUuid: theme?.uuid ?? null,
              customDomainUuids: undefined,
              rules: undefined,
              name: undefined,
              state: undefined,
              title: undefined,
              subtitle: undefined,
              questions: undefined,
            },
          },
        });
      }}
    />
  );
};
